"use client";

import { useEffect, useState } from "react";

import {
  dotMarkRadius,
  markShapeOf,
  xMarkSegments,
} from "@/lib/annotation-mark-shape";
import {
  medicationNumberFontSize,
  medicationNumberOutline,
} from "@/lib/annotation-number";
import {
  pinAnchorRadius,
  pinBadgeCornerRadius,
  pinBadgeFontSize,
  pinBadgeSize,
  pinBadgeTextColor,
  pinLabelText,
  pinLeaderEnd,
  pinScaleForImage,
} from "@/lib/annotation-pin";
import { dashArray } from "@/lib/annotation-stroke";

import { Annotation } from "@/types/annotation";

export const AnnotationOverlay = ({
  annotations,
  imageFile,
  imageUrl,
  aspectRatio,
  containerSize = 200,
}: {
  annotations: Annotation[];
  imageFile?: File;
  imageUrl?: string;
  /** Aspect ratio (width / height) of the image actually rendered on screen.
   * When provided it wins over measuring `imageUrl`/`imageFile` here: it stays
   * correct even when a thumbnail 404s and the caller falls back to the
   * full-resolution image (a different aspect ratio), and it avoids decoding a
   * second copy of the image just to read its dimensions. */
  aspectRatio?: number;
  containerSize?: number;
}) => {
  const [measuredAspectRatio, setMeasuredAspectRatio] = useState(1);
  const [mounted, setMounted] = useState(false);

  const hasProvidedAspectRatio = !!aspectRatio && aspectRatio > 0;
  const effectiveAspectRatio = hasProvidedAspectRatio
    ? aspectRatio
    : measuredAspectRatio;

  useEffect(() => {
    setMounted(true);
    // The caller already knows the displayed image's aspect ratio — trust it
    // instead of fetching and decoding the image a second time here.
    if (hasProvidedAspectRatio) return;
    if (!imageFile && !imageUrl) return;

    const img = globalThis.Image
      ? new globalThis.Image()
      : new (window as any).Image();

    let url: string;
    let shouldRevoke = false;

    if (imageFile) {
      url = URL.createObjectURL(imageFile);
      shouldRevoke = true;
    } else if (imageUrl) {
      url = imageUrl;
    } else {
      return;
    }

    img.onload = () => {
      setMeasuredAspectRatio(img.width / img.height);
      if (shouldRevoke) {
        URL.revokeObjectURL(url);
      }
    };

    img.onerror = () => {
      if (shouldRevoke) {
        URL.revokeObjectURL(url);
      }
    };

    img.src = url;

    return () => {
      if (shouldRevoke) {
        URL.revokeObjectURL(url);
      }
    };
  }, [imageFile, imageUrl, hasProvidedAspectRatio]);

  if (
    !mounted ||
    !annotations ||
    annotations.length === 0 ||
    (!imageFile && !imageUrl)
  )
    return null;

  // Calculate the actual rendered image dimensions within the container
  // considering object-cover behavior
  const containerAspectRatio = 1; // Square container
  let renderedWidth,
    renderedHeight,
    offsetX = 0,
    offsetY = 0;

  if (effectiveAspectRatio > containerAspectRatio) {
    // Image is wider - it will be cropped horizontally
    renderedHeight = containerSize;
    renderedWidth = containerSize * effectiveAspectRatio;
    offsetX = -(renderedWidth - containerSize) / 2;
  } else {
    // Image is taller or square - it will be cropped vertically
    renderedWidth = containerSize;
    renderedHeight = containerSize / effectiveAspectRatio;
    offsetY = -(renderedHeight - containerSize) / 2;
  }

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${containerSize} ${containerSize}`}
        className="absolute inset-0"
      >
        <g transform={`translate(${offsetX}, ${offsetY})`}>
          {annotations.map((annotation) => {
            switch (annotation.type) {
              case "pen": {
                const pathData = annotation.points
                  .map((point, index) => {
                    const x = point.x * renderedWidth;
                    const y = point.y * renderedHeight;
                    return `${index === 0 ? "M" : "L"} ${x} ${y}`;
                  })
                  .join(" ");

                return (
                  <path
                    key={annotation.id}
                    d={pathData}
                    stroke={annotation.color}
                    strokeWidth={annotation.strokeWidth * 0.8}
                    strokeDasharray={dashArray(
                      annotation.strokeStyle,
                      annotation.strokeWidth * 0.8,
                    )}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                );
              }

              case "arrow": {
                const startX = annotation.start.x * renderedWidth;
                const startY = annotation.start.y * renderedHeight;
                const endX = annotation.end.x * renderedWidth;
                const endY = annotation.end.y * renderedHeight;

                // Calculate arrow head for thumbnail
                const angle = Math.atan2(endY - startY, endX - startX);
                const arrowLength = 8; // Smaller for thumbnail
                const arrowAngle = Math.PI / 6;

                const arrowHead1X =
                  endX - arrowLength * Math.cos(angle - arrowAngle);
                const arrowHead1Y =
                  endY - arrowLength * Math.sin(angle - arrowAngle);
                const arrowHead2X =
                  endX - arrowLength * Math.cos(angle + arrowAngle);
                const arrowHead2Y =
                  endY - arrowLength * Math.sin(angle + arrowAngle);

                return (
                  <g key={annotation.id}>
                    {/* Arrow line */}
                    <line
                      x1={startX}
                      y1={startY}
                      x2={endX}
                      y2={endY}
                      stroke={annotation.color}
                      strokeWidth={annotation.strokeWidth * 0.8}
                      strokeDasharray={dashArray(
                        annotation.strokeStyle,
                        annotation.strokeWidth * 0.8,
                      )}
                      strokeLinecap="round"
                    />
                    {/* Arrow head */}
                    <path
                      d={`M ${arrowHead1X} ${arrowHead1Y} L ${endX} ${endY} L ${arrowHead2X} ${arrowHead2Y}`}
                      stroke={annotation.color}
                      strokeWidth={annotation.strokeWidth * 0.8}
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                );
              }

              case "circle": {
                const centerX = annotation.center.x * renderedWidth;
                const centerY = annotation.center.y * renderedHeight;
                const radius =
                  annotation.radius * Math.min(renderedWidth, renderedHeight);
                const shape = markShapeOf(annotation);

                if (shape === "dot") {
                  return (
                    <circle
                      key={annotation.id}
                      cx={centerX}
                      cy={centerY}
                      r={dotMarkRadius(radius)}
                      fill={annotation.color}
                    />
                  );
                }

                if (shape === "x") {
                  return (
                    <g key={annotation.id}>
                      {xMarkSegments(centerX, centerY, radius).map(
                        ([x1, y1, x2, y2], i) => (
                          <line
                            key={i}
                            x1={x1}
                            y1={y1}
                            x2={x2}
                            y2={y2}
                            stroke={annotation.color}
                            strokeWidth={annotation.strokeWidth * 0.8}
                            strokeDasharray={dashArray(
                              annotation.strokeStyle,
                              annotation.strokeWidth * 0.8,
                            )}
                            strokeLinecap="round"
                          />
                        ),
                      )}
                    </g>
                  );
                }

                return (
                  <circle
                    key={annotation.id}
                    cx={centerX}
                    cy={centerY}
                    r={radius}
                    stroke={annotation.color}
                    strokeWidth={annotation.strokeWidth * 0.8}
                    strokeDasharray={dashArray(
                      annotation.strokeStyle,
                      annotation.strokeWidth * 0.8,
                    )}
                    fill="none"
                  />
                );
              }

              case "comment": {
                const x = annotation.position.x * renderedWidth;
                const y = annotation.position.y * renderedHeight;
                const boxWidth = 60;
                const lineHeight = 10;
                const text = annotation.text;
                const truncated =
                  text.length > 20 ? text.substring(0, 20) + "..." : text;
                // Simple line split for thumbnail
                const lines: string[] = [];
                const maxCharsPerLine = 12;
                for (let i = 0; i < truncated.length; i += maxCharsPerLine) {
                  lines.push(truncated.substring(i, i + maxCharsPerLine));
                }
                const boxHeight = lines.length * lineHeight + 6;

                return (
                  <g key={annotation.id}>
                    <rect
                      x={x}
                      y={y}
                      width={boxWidth}
                      height={boxHeight}
                      fill="transparent"
                      stroke={annotation.color}
                      strokeWidth={0.5}
                      strokeDasharray="2 1.5"
                      rx={2}
                    />
                    {lines.map((line, i) => (
                      <text
                        key={i}
                        x={x + 3}
                        y={y + 8 + i * lineHeight}
                        fontSize="7"
                        fill={annotation.color}
                      >
                        {line}
                      </text>
                    ))}
                  </g>
                );
              }

              case "dot": {
                const x = annotation.position.x * renderedWidth;
                const y = annotation.position.y * renderedHeight;
                const radius = annotation.radius
                  ? annotation.radius * Math.min(renderedWidth, renderedHeight)
                  : 2; // Small default radius for thumbnail

                return (
                  <circle
                    key={annotation.id}
                    cx={x}
                    cy={y}
                    r={radius}
                    fill={annotation.color}
                    stroke="white"
                    strokeWidth={0.5}
                  />
                );
              }

              case "pin": {
                const scale = pinScaleForImage(
                  Math.min(renderedWidth, renderedHeight),
                );
                const siteX = annotation.position.x * renderedWidth;
                const siteY = annotation.position.y * renderedHeight;
                const badgeX = annotation.label.x * renderedWidth;
                const badgeY = annotation.label.y * renderedHeight;
                const labelText = pinLabelText(annotation);
                const badge = pinBadgeSize(labelText, scale);
                const leaderEnd = pinLeaderEnd(
                  { x: siteX, y: siteY },
                  { x: badgeX, y: badgeY },
                  badge,
                );
                const stemWidth = Math.max(annotation.strokeWidth * 0.4, 0.5);

                return (
                  <g key={annotation.id}>
                    <line
                      x1={siteX}
                      y1={siteY}
                      x2={leaderEnd.x}
                      y2={leaderEnd.y}
                      stroke={annotation.color}
                      strokeWidth={stemWidth}
                      strokeDasharray={dashArray(
                        annotation.strokeStyle,
                        stemWidth,
                      )}
                      strokeLinecap="round"
                    />
                    <circle
                      cx={siteX}
                      cy={siteY}
                      r={pinAnchorRadius(scale)}
                      fill={annotation.color}
                      stroke="white"
                      strokeWidth={0.4}
                    />
                    <rect
                      x={badgeX - badge.width / 2}
                      y={badgeY - badge.height / 2}
                      width={badge.width}
                      height={badge.height}
                      rx={pinBadgeCornerRadius(scale)}
                      fill={annotation.color}
                    />
                    <text
                      x={badgeX}
                      y={badgeY}
                      fontSize={pinBadgeFontSize(scale)}
                      fill={pinBadgeTextColor(annotation.color)}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontWeight="bold"
                    >
                      {labelText}
                    </text>
                  </g>
                );
              }

              case "syringeLine": {
                // Render syringe line as a freeform path (similar to pen)
                const pathData = annotation.points
                  .map((point, index) => {
                    const x = point.x * renderedWidth;
                    const y = point.y * renderedHeight;
                    return `${index === 0 ? "M" : "L"} ${x} ${y}`;
                  })
                  .join(" ");

                return (
                  <path
                    key={annotation.id}
                    d={pathData}
                    stroke={annotation.color}
                    strokeWidth={(annotation.strokeWidth + 1) * 0.8}
                    strokeDasharray={dashArray(
                      annotation.strokeStyle,
                      (annotation.strokeWidth + 1) * 0.8,
                    )}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                );
              }

              case "medication": {
                const amountText = String(annotation.amount);
                const baseFontSize = medicationNumberFontSize(
                  annotation.strokeWidth,
                  pinScaleForImage(Math.min(renderedWidth, renderedHeight)),
                );

                if (annotation.shape === "circle" && annotation.position) {
                  const x = annotation.position.x * renderedWidth;
                  const y = annotation.position.y * renderedHeight;

                  return (
                    <g key={annotation.id}>
                      {/* Just the amount number with white outline */}
                      <text
                        x={x}
                        y={y}
                        fontSize={baseFontSize}
                        fill={annotation.color}
                        stroke="white"
                        strokeWidth={medicationNumberOutline(baseFontSize)}
                        paintOrder="stroke"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontWeight="bold"
                      >
                        {amountText}
                      </text>
                    </g>
                  );
                } else if (
                  annotation.shape === "arrow" &&
                  annotation.start &&
                  annotation.end
                ) {
                  const startX = annotation.start.x * renderedWidth;
                  const startY = annotation.start.y * renderedHeight;
                  const endX = annotation.end.x * renderedWidth;
                  const endY = annotation.end.y * renderedHeight;

                  // Calculate arrow head for thumbnail
                  const angle = Math.atan2(endY - startY, endX - startX);
                  const arrowLength = 3 + (annotation.strokeWidth || 1) * 1.5;
                  const arrowAngle = Math.PI / 6;

                  const arrowHead1X =
                    endX - arrowLength * Math.cos(angle - arrowAngle);
                  const arrowHead1Y =
                    endY - arrowLength * Math.sin(angle - arrowAngle);
                  const arrowHead2X =
                    endX - arrowLength * Math.cos(angle + arrowAngle);
                  const arrowHead2Y =
                    endY - arrowLength * Math.sin(angle + arrowAngle);

                  return (
                    <g key={annotation.id}>
                      {/* Arrow line */}
                      <line
                        x1={startX}
                        y1={startY}
                        x2={endX}
                        y2={endY}
                        stroke={annotation.color}
                        strokeWidth={Math.max(
                          annotation.strokeWidth * 0.8,
                          0.5,
                        )}
                        strokeLinecap="round"
                      />
                      {/* Arrow head */}
                      <path
                        d={`M ${arrowHead1X} ${arrowHead1Y} L ${endX} ${endY} L ${arrowHead2X} ${arrowHead2Y} Z`}
                        stroke={annotation.color}
                        fill={annotation.color}
                        strokeWidth={Math.max(
                          annotation.strokeWidth * 0.5,
                          0.3,
                        )}
                      />
                      {/* Amount number at start with white outline */}
                      <text
                        x={startX}
                        y={startY}
                        fontSize={baseFontSize}
                        fill={annotation.color}
                        stroke="white"
                        strokeWidth={medicationNumberOutline(baseFontSize)}
                        paintOrder="stroke"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontWeight="bold"
                      >
                        {amountText}
                      </text>
                    </g>
                  );
                }
                return null;
              }

              default:
                return null;
            }
          })}
        </g>
      </svg>
    </div>
  );
};
